import React, { useEffect, useState } from 'react';

import clsx from 'clsx';

import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';
import EventIcon from '@material-ui/icons/Event';
import { KeyboardDatePicker } from '@material-ui/pickers';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import InputAdornment from '@material-ui/core/InputAdornment';
import CircularProgress from '@material-ui/core/CircularProgress';
import { dateFormatsMapping } from '../../services/constants';
import { INTAKE_FORM_STATUS } from '../../services/constants';

// @ts-ignore
import CurrencyTextField from '@unicef/material-ui-currency-textfield';

import { useStyles } from './styles';
import Select from '@material-ui/core/Select/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl/FormControl';
import RadioGroup from '@material-ui/core/RadioGroup/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel/FormControlLabel';
import Radio from '@material-ui/core/Radio/Radio';
import moment from 'moment';
import Tooltip from '@material-ui/core/Tooltip/Tooltip';

const FormRow = React.memo(
    ({
        rowData,
        isFormInstance,
        putFormChange,
        formId,
        onValid,
        onInvalid,
        formStatus,
        isFormPreview,
        putFormRequest,
        putSelectedCountry,
        countryList,
        selectedCountry,
        loadingCountry,
    }: any) => {
        const classes = useStyles();
        let stateOptions = [];
        const renderer = rowData.renderer;
        let renderMask = 'YYYY-MM-DD';
        if (rowData.renderer === 'date') {
            renderMask = dateFormatsMapping[rowData.fieldFormat];
        }
        if (rowData.renderer === 'state-list') {
            const selectedCountryItem = countryList?.find((country) => {
                return country.id === parseInt(selectedCountry, 10);
            });
            stateOptions = selectedCountryItem?.states || [];
        }
        const id = rowData.id || rowData.fieldId;
        const listItemIdName = isFormInstance ? 'label' : 'id';
        const isDisabled = formStatus === INTAKE_FORM_STATUS.SUBMITTED || isFormPreview;

        // If we are previewing a submitted form, the value field is called fieldValue, not value, so we need to determine that
        const valueProperty = isFormPreview ? 'fieldValue' : 'value';

        const [error, setError] = useState('');
        const [changeTimeout, setChangeTimeout] = React.useState<any>();
        const [isSaving, setIsSaving] = React.useState<boolean>(false);

        const selectedDate = rowData[valueProperty] ? moment(rowData[valueProperty]) : null;
        const textValue = rowData[valueProperty] || '';
        const radioValue = rowData[valueProperty] || '';

        let selectValue;
        if (rowData.renderer === 'country-list') {
            selectValue = loadingCountry ? '' : rowData[valueProperty] || '';
        } else if (rowData.renderer === 'state-list') {
            selectValue = !stateOptions.length ? '' : rowData[valueProperty] || '';
        } else {
            selectValue = rowData[valueProperty] || '';
        }

        useEffect(() => {
            if (rowData.required && !rowData[valueProperty]) {
                onInvalid(id);
            }
        }, [id, onInvalid, rowData, valueProperty]);

        useEffect(() => {
            if (renderer === 'country-list' && selectValue) {
                putSelectedCountry(selectValue);
            }
        }, [renderer, selectValue, putSelectedCountry]);

        const handleDateChange = (date: any) => {
            // we will set the value regardless of the conditions, since if it's wrong, the UI will display it
            putFormRequest(rowData.fieldId, date);
            if (rowData.required && !moment(date).isValid()) {
                setError('This field is required');
                onInvalid(id);
            } else {
                setError('');
                onValid(id);
                if (isFormInstance) {
                    // we set and clear timeouts to debounce the change event
                    if (changeTimeout) {
                        window.clearTimeout(changeTimeout);
                    }
                    // we won't call the API if the value is required and coming empty
                    if (!rowData.required || !!date) {
                        setChangeTimeout(
                            window.setTimeout(() => {
                                setIsSaving(true);
                                //putFormChange returns a promise so we can handle the saving circular indicator
                                putFormChange(formId, rowData.fieldId, date).then(
                                    () => {
                                        setIsSaving(false);
                                    },
                                    () => setIsSaving(false),
                                );
                            }, 1000),
                        );
                    }
                }
            }
        };

        const handleTextChange = (e: any, value?: any) => {
            //save event inside constant so that it's accessible even after timeout
            const { target } = e;
            if (target.required && !target.value.length) {
                setError('This field is required');
                onInvalid(id);
            } else {
                setError('');
                onValid(id);
            }

            if (
                target.value.length &&
                rowData.maxLength > 0 &&
                target.value.length > rowData.maxLength
            ) {
                setError(
                    'This field exceeds the maximum length of ' + rowData.maxLength + ' characters',
                );
                onInvalid(id);
            }
            // only run this calls to the server if we are working with a form instance
            if (isFormInstance) {
                // we set and clear timeouts to debounce the change event
                if (changeTimeout) {
                    window.clearTimeout(changeTimeout);
                }
                // If you are putting a number, make sure we are not allowing decimals
                const putValue =
                    target.type === 'number' ? parseInt(target.value, 10) : target.value;
                putFormRequest(rowData.fieldId, putValue);
                // we won't call the API if the value is required and coming empty
                if (rowData.maxLength > 0 && target.value.length > 0) {
                    if (target.value.length <= rowData.maxLength) {
                        setChangeTimeout(
                            window.setTimeout(function () {
                                setIsSaving(true);
                                let changedValue;
                                // value will come
                                if (!!value || value === 0) {
                                    changedValue = value;
                                } else {
                                    // If you are putting a number, make sure we are not allowing decimals
                                    changedValue =
                                        target.type === 'number'
                                            ? parseInt(target.value, 10)
                                            : target.value;
                                }
                                //putFormChange returns a promise so we can handle the saving circular indicator
                                putFormChange(formId, rowData.fieldId, changedValue).then(
                                    () => {
                                        setIsSaving(false);
                                    },
                                    () => setIsSaving(false),
                                );
                            }, 1000),
                        );
                    }
                } else {
                    if (!rowData.required || !!target.value) {
                        setChangeTimeout(
                            window.setTimeout(function () {
                                setIsSaving(true);
                                let changedValue;
                                // value will come
                                if (!!value || value === 0) {
                                    changedValue = value;
                                } else {
                                    // If you are putting a number, make sure we are not allowing decimals
                                    changedValue =
                                        target.type === 'number'
                                            ? parseInt(target.value, 10)
                                            : target.value;
                                }
                                //putFormChange returns a promise so we can handle the saving circular indicator
                                putFormChange(formId, rowData.fieldId, changedValue).then(
                                    () => {
                                        setIsSaving(false);
                                    },
                                    () => setIsSaving(false),
                                );
                            }, 1000),
                        );
                    }
                }
            }
            // we will set the value regardless of the conditions, since if it's wrong, the UI will display it
            // setTextValue(target.value);
        };

        const handleSelectChange = (e: any) => {
            //save event inside constant so that it's accessible even after timeout
            const { target } = e;
            if (rowData.required && !target.value && target.value !== 0) {
                setError('This field is required');
                onInvalid(id);
            } else {
                setError('');
                onValid(id);
            }
            // only run this calls to the server if we are working with a form instance
            if (isFormInstance) {
                // we set and clear timeouts to debounce the change event
                if (changeTimeout) {
                    window.clearTimeout(changeTimeout);
                }
                putFormRequest(rowData.fieldId, target.value);
                // we won't call the API if the value is required and coming empty
                if (!rowData.required || !!target.value || target.value === 0) {
                    setChangeTimeout(
                        window.setTimeout(() => {
                            setIsSaving(true);
                            //putFormChange returns a promise so we can handle the saving circular indicator
                            putFormChange(formId, rowData.fieldId, target.value).then(
                                () => {
                                    setIsSaving(false);
                                },
                                () => setIsSaving(false),
                            );
                        }, 1000),
                    );
                }
            }
            // we will set the value regardless of the conditions, since if it's wrong, the UI will display it
            // setSelectValue(target.value);
        };
        const handleCountrySelectChange = (e: any) => {
            //save event inside constant so that it's accessible even after timeout
            const { target } = e;
            if (rowData.required && !target.value && target.value !== 0) {
                setError('This field is required');
                onInvalid(id);
            } else {
                setError('');
                onValid(id);
            }
            // only run this calls to the server if we are working with a form instance
            if (isFormInstance) {
                // we set and clear timeouts to debounce the change event
                if (changeTimeout) {
                    window.clearTimeout(changeTimeout);
                }
                putFormRequest(rowData.fieldId, target.value);
                // we won't call the API if the value is required and coming empty
                if (!rowData.required || !!target.value || target.value === 0) {
                    setChangeTimeout(
                        window.setTimeout(() => {
                            setIsSaving(true);
                            //putFormChange returns a promise so we can handle the saving circular indicator
                            putFormChange(formId, rowData.fieldId, target.value).then(
                                () => {
                                    setIsSaving(false);
                                },
                                () => setIsSaving(false),
                            );
                        }, 1000),
                    );
                }
            }
            // we will set the value regardless of the conditions, since if it's wrong, the UI will display it
            // setSelectValue(target.value);
        };

        const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            //save event inside constant so that it's accesible even after timeout
            const { target } = e;
            if (rowData.required && !target.value && target.value !== '0') {
                setError('This field is required');
                onInvalid(id);
            } else {
                setError('');
                onValid(id);
            }
            if (isFormInstance) {
                // we set and clear timeouts to debounce the change event
                if (changeTimeout) {
                    window.clearTimeout(changeTimeout);
                }
                putFormRequest(rowData.fieldId, target.value);
                // we won't call the API if the value is required and coming empty
                if (!rowData.required || !!target.value || target.value === '0') {
                    setChangeTimeout(
                        window.setTimeout(() => {
                            setIsSaving(true);
                            //putFormChange returns a promise so we can handle the saving circular indicator
                            putFormChange(formId, rowData.fieldId, target.value).then(
                                () => {
                                    setIsSaving(false);
                                },
                                () => setIsSaving(false),
                            );
                        }, 1000),
                    );
                }
            }
            // setRadioValue(target.value);
        };

        const renderInputElement = () => {
            switch (rowData.renderer) {
                case 'textbox-singleline':
                    return (
                        <TextField
                            id={id.toString()}
                            error={!!error}
                            classes={{ root: classes.rowInputRoot }}
                            placeholder="Your answer"
                            onChange={handleTextChange}
                            required={rowData.required}
                            helperText={error ? error : <span className={classes.placeholderDiv} />}
                            value={textValue}
                            disabled={isDisabled}
                            InputProps={{
                                endAdornment: isSaving ? (
                                    <InputAdornment position="end">
                                        <CircularProgress size={20} />
                                    </InputAdornment>
                                ) : (
                                    ''
                                ),
                            }}
                        />
                    );
                case 'textbox-multiline':
                    return (
                        <TextField
                            id={id.toString()}
                            multiline
                            error={!!error}
                            rows={4}
                            classes={{ root: classes.rowInputRoot }}
                            placeholder="Your answer"
                            onChange={handleTextChange}
                            required={rowData.required}
                            helperText={error ? error : <span className={classes.placeholderDiv} />}
                            value={textValue}
                            disabled={isDisabled}
                            InputProps={{
                                endAdornment: isSaving ? (
                                    <InputAdornment position="end">
                                        <CircularProgress size={20} />
                                    </InputAdornment>
                                ) : (
                                    ''
                                ),
                            }}
                        />
                    );
                case 'textbox-url':
                    return (
                        <button className={classes.linkButton} type="button">
                            Link
                        </button>
                    );
                case 'list':
                    return (
                        <FormControl className={classes.rowInputRoot} error={!!error}>
                            <Select
                                labelId="id"
                                id={id.toString()}
                                onChange={handleSelectChange}
                                required={rowData.required}
                                value={selectValue}
                                disabled={isDisabled}
                                endAdornment={
                                    isSaving ? (
                                        <InputAdornment
                                            className={classes.selectAdornment}
                                            position="end"
                                        >
                                            <CircularProgress size={20} />
                                        </InputAdornment>
                                    ) : (
                                        ''
                                    )
                                }
                            >
                                <MenuItem key="0" value="Select" selected>
                                    <em>Select</em>
                                </MenuItem>
                                {rowData.listValues?.map((value: any, i: any) => {
                                    return (
                                        <MenuItem
                                            key={value.listValueId}
                                            value={value[listItemIdName]}
                                        >
                                            {value.label}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                            {error ? (
                                <FormHelperText>{error}</FormHelperText>
                            ) : (
                                <div className={classes.placeholderDiv} />
                            )}
                        </FormControl>
                    );
                case 'country-list':
                    return (
                        <FormControl className={classes.rowInputRoot} error={!!error}>
                            <Select
                                labelId="id"
                                id={id.toString()}
                                onChange={handleCountrySelectChange}
                                required={rowData.required}
                                value={selectValue}
                                disabled={isDisabled}
                                endAdornment={
                                    isSaving ? (
                                        <InputAdornment
                                            className={classes.selectAdornment}
                                            position="end"
                                        >
                                            <CircularProgress size={20} />
                                        </InputAdornment>
                                    ) : (
                                        ''
                                    )
                                }
                            >
                                <MenuItem value="" selected>
                                    <em>Select</em>
                                </MenuItem>
                                {countryList?.map((value: any, i: any) => {
                                    return (
                                        <MenuItem key={value.id} value={value.id}>
                                            {value.longName}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                            {error ? (
                                <FormHelperText>{error}</FormHelperText>
                            ) : (
                                <div className={classes.placeholderDiv} />
                            )}
                        </FormControl>
                    );
                case 'state-list':
                    return (
                        <FormControl className={classes.rowInputRoot} error={!!error}>
                            <Select
                                labelId="id"
                                id={id.toString()}
                                onChange={handleSelectChange}
                                required={rowData.required}
                                value={selectValue}
                                disabled={isDisabled}
                                endAdornment={
                                    isSaving ? (
                                        <InputAdornment
                                            className={classes.selectAdornment}
                                            position="end"
                                        >
                                            <CircularProgress size={20} />
                                        </InputAdornment>
                                    ) : (
                                        ''
                                    )
                                }
                            >
                                <MenuItem value="" selected>
                                    <em>Select</em>
                                </MenuItem>
                                {stateOptions?.map((value: any) => {
                                    return (
                                        <MenuItem key={value.id} value={value.id}>
                                            {value.longName}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                            {error ? (
                                <FormHelperText>{error}</FormHelperText>
                            ) : (
                                <div className={classes.placeholderDiv} />
                            )}
                        </FormControl>
                    );
                case 'date':
                    return (
                        <KeyboardDatePicker
                            variant="inline"
                            required={rowData.required}
                            format={renderMask}
                            margin="normal"
                            id="date-picker-inline"
                            onChange={handleDateChange}
                            value={selectedDate}
                            disabled={isDisabled}
                            // inputValue={inputValue}
                            KeyboardButtonProps={{
                                'aria-label': 'change date',
                            }}
                            keyboardIcon={
                                isSaving ? (
                                    <InputAdornment
                                        position="end"
                                        className={classes.calendarAdornment}
                                    >
                                        <CircularProgress size={20} />
                                    </InputAdornment>
                                ) : (
                                    <EventIcon />
                                )
                            }
                        />
                    );
                case 'money':
                    return (
                        <CurrencyTextField
                            className={classes.rowInputRoot}
                            disabled={isDisabled}
                            error={!!error}
                            variant="standard"
                            currencySymbol="$"
                            minimumValue="0"
                            outputFormat="number"
                            decimalCharacter="."
                            digitGroupSeparator=","
                            onBlur={(event: any, value: any) => {
                                handleTextChange(event, value);
                            }}
                            required={rowData.required}
                            helperText={error ? error : <span className={classes.placeholderDiv} />}
                            value={textValue}
                            InputProps={{
                                endAdornment: isSaving ? (
                                    <InputAdornment position="end">
                                        <CircularProgress size={20} />
                                    </InputAdornment>
                                ) : (
                                    ''
                                ),
                            }}
                        />
                    );
                case 'number':
                    return (
                        <TextField
                            id={id.toString()}
                            disabled={isDisabled}
                            error={!!error}
                            type="number"
                            classes={{ root: classes.rowInputRoot }}
                            placeholder="Your answer"
                            onChange={handleTextChange}
                            required={rowData.required}
                            helperText={error ? error : <span className={classes.placeholderDiv} />}
                            value={textValue}
                            InputProps={{
                                endAdornment: isSaving ? (
                                    <InputAdornment position="end">
                                        <CircularProgress size={20} />
                                    </InputAdornment>
                                ) : (
                                    ''
                                ),
                            }}
                        />
                    );
                case 'option-list':
                    return (
                        <>
                            <FormControl component="fieldset">
                                <RadioGroup
                                    aria-label={rowData.displayLabel}
                                    name={rowData.displayLabel}
                                    value={radioValue}
                                    onChange={handleRadioChange}
                                >
                                    {rowData.listValues?.map((value: any, i: any) => {
                                        return (
                                            <FormControlLabel
                                                key={value.listValueId}
                                                disabled={isDisabled}
                                                value={value[listItemIdName].toString()}
                                                control={<Radio />}
                                                label={
                                                    <div>
                                                        {value.label}
                                                        {isSaving &&
                                                            radioValue ===
                                                                value[
                                                                    listItemIdName
                                                                ].toString() && (
                                                                <span
                                                                    className={
                                                                        classes.savingContainer
                                                                    }
                                                                >
                                                                    <CircularProgress size={15} />
                                                                </span>
                                                            )}
                                                    </div>
                                                }
                                            />
                                        );
                                    })}
                                </RadioGroup>
                            </FormControl>
                        </>
                    );
            }
        };

        return (
            <Paper
                classes={{ root: classes.formRowRoot }}
                elevation={0}
                className={clsx({ [classes.error]: !!error })}
            >
                <div className={classes.rowLabel}>
                    {rowData.displayLabel}{' '}
                    {rowData.required && <span className={classes.required}>*</span>}
                    {rowData.description && (
                        <span>
                            <Tooltip title={rowData.description} aria-label={rowData.description}>
                                <HelpOutlineIcon fontSize="small" />
                            </Tooltip>
                        </span>
                    )}
                </div>
                {renderInputElement()}
            </Paper>
        );
    },
    (prevProps, nextProps) => {
        if (prevProps.rowData.renderer === 'country-list') {
            return (
                prevProps.rowData.value === nextProps.rowData.value &&
                prevProps.formStatus === nextProps.formStatus &&
                prevProps.isFormPreview === nextProps.isFormPreview &&
                prevProps.countryList === nextProps.countryList
            );
        } else if (prevProps.rowData.renderer === 'state-list') {
            return (
                prevProps.rowData.value === nextProps.rowData.value &&
                prevProps.formStatus === nextProps.formStatus &&
                prevProps.isFormPreview === nextProps.isFormPreview &&
                prevProps.selectedCountry === nextProps.selectedCountry &&
                prevProps.countryList === nextProps.countryList
            );
        } else {
            return (
                prevProps.rowData.value === nextProps.rowData.value &&
                prevProps.formStatus === nextProps.formStatus &&
                prevProps.isFormPreview === nextProps.isFormPreview
            );
        }
    },
);

export default FormRow;
